<template>
    <v-card class="" data-testid="comments">
      <div v-if="loading" class="text-center pa-6" data-testid="comments__loading">
        <v-progress-circular indeterminate color="primary" size="32" data-testid="comments__loading-spinner" />
        <div class="mt-2" data-testid="comments__loading-text">Loading comments...</div>
      </div>
      <div v-else ref="chatContainer" class="pa-2 comments__list" style="max-height: 30vh; overflow: scroll" data-testid="comments__list">
        <div
          v-for="(item, index) in conversationList"
          :key="index"
          class="comments__item"
          :class="isSelfMessage(item) ? 'comments__item--self' : 'comments__item--other'"
          :data-testid="`comments__item-${index}`"
          :data-self="isSelfMessage(item)"
        >
          <div
            class="comments__avatar-col"
            :data-testid="isSelfMessage(item) ? `comments__avatar-col-self-${index}` : `comments__avatar-col-other-${index}`"
          >
            <v-tooltip v-if="shouldShowAvatar(index)" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-avatar
                  size="30"
                  elevation="10"
                  color="indigo"
                  v-bind="attrs"
                  v-on="on"
                  :data-testid="isSelfMessage(item) ? `comments__avatar-self-${index}` : `comments__avatar-other-${index}`"
                >
                  <v-img
                    v-if="shouldShowProfileImage(item)"
                    alt="Avatar"
                    width="30"
                    height="30"
                    :src="item.user_detail.profile_url"
                    @error="onProfileImageError(item)"
                    :data-testid="isSelfMessage(item) ? `comments__avatar-self-img-${index}` : `comments__avatar-other-img-${index}`"
                  />
                  <span
                    v-else
                    class="white--text"
                    :data-testid="isSelfMessage(item) ? `comments__avatar-self-initial-${index}` : `comments__avatar-other-initial-${index}`"
                  >
                    {{ getUserInitials(item) }}
                  </span>
                </v-avatar>
              </template>
              <span>{{ getUserName(item) }}</span>
            </v-tooltip>
            <span
              v-else
              class="comments__avatar-spacer"
              :data-testid="isSelfMessage(item) ? `comments__avatar-spacer-self-${index}` : `comments__avatar-spacer-other-${index}`"
            />
          </div>

          <div
            class="comments__content-col"
            :data-testid="isSelfMessage(item) ? `comments__content-col-self-${index}` : `comments__content-col-other-${index}`"
          >
            <div
              class="comments__meta"
              :class="isSelfMessage(item) ? 'comments__meta--self' : 'comments__meta--other'"
              :data-testid="isSelfMessage(item) ? `comments__item-self-header-${index}` : `comments__item-other-header-${index}`"
            >
              <span
                v-if="shouldShowAvatar(index)"
                class="comments__thread-name"
                :data-testid="isSelfMessage(item) ? `comments__item-self-name-${index}` : `comments__item-other-name-${index}`"
              >
                {{ getUserName(item) }}
              </span>
              <span
                class="timeFont"
                :data-testid="isSelfMessage(item) ? `comments__timestamp-self-${index}` : `comments__timestamp-other-${index}`"
              >
                {{ $nova.formatDateTime(item.created_at, 'DD MMM yyyy hh:mm A') }}
              </span>
            </div>

            <v-card
              class="comments__message-card shadow-off"
              :class="isSelfMessage(item) ? 'comments__message-card--self' : 'comments__message-card--other'"
              :color="isSelfMessage(item) ? 'purpleborder' : 'lightgray'"
              :data-testid="isSelfMessage(item) ? `comments__message-self-card-${index}` : `comments__message-other-card-${index}`"
            >
              <v-card-text
                class="shadow-off pa-3"
                :data-testid="isSelfMessage(item) ? `comments__message-self-text-wrapper-${index}` : `comments__message-other-text-wrapper-${index}`"
              >
                <div
                  class="self-message"
                  :id="isSelfMessage(item) ? `sent-chat-message-${index}` : `receive-chat-message-${index}`"
                  v-html="formatCommentMessage(item.value)"
                  :data-testid="isSelfMessage(item) ? `comments__message-self-text-${index}` : `comments__message-other-text-${index}`"
                />
              </v-card-text>
            </v-card>
          </div>
        </div>
        <div
          v-if="(conversationList || []).length === 0"
          class="d-flex flex-column align-center justify-center all-height"
          data-testid="comments__empty"
        >
          <v-spacer></v-spacer>
          <p class="text-font-size-md grey--text">Be the first one to comment</p>
          <v-spacer></v-spacer>
        </div>
      </div>
      <div class="d-flex align-center all-width pa-2 gap-2" v-if="!loading && canComment" data-testid="comments__input-wrapper">
        <lb-tagtextbox
          v-model="localQueryMsg"
          label=""
          placeholder="Type your message here"
          :hidedetails="true"
          :allTagsList="tagsList"
          :disabled="sendingComment"
          data-testid="comments__input"
          class="flex-grow-1"
        />
        <v-tooltip bottom content-class="tooltip-bottom" open-delay="500" :disabled="showOptions" data-testid="comments__mention-tooltip">
          <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
            <div v-bind="tooltipAttrs" v-on="tooltipOn" style="display: inline-block;">
              <v-menu v-model="showOptions" :close-on-content-click="false" offset-y :nudge-width="200" data-testid="comments__mention-menu">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon class="primaryBtn" v-bind="attrs" v-on="on" @click="showOptions = !showOptions" data-testid="comments__mention-btn">
                    <v-icon data-testid="comments__mention-icon">@</v-icon>
                    <v-badge v-if="selectedUsers.length" :content="selectedUsers.length" color="red" overlap class="user-badge" data-testid="comments__mention-badge">
                    </v-badge>
                  </v-btn>
                </template>
                <v-list class="user-list-menu" data-testid="comments__mention-user-list">
                  <v-text-field
                    v-model="userSearch"
                    placeholder="Search users..."
                    dense
                    outlined
                    hide-details
                    class="mx-2 my-2"
                    data-testid="comments__mention-search"
                  />
                  <v-list-item v-for="user in filteredUsersList" :key="user._id" :data-testid="`comments__mention-user-${user._id}`">
                    <v-checkbox
                      v-model="selectedUsers"
                      :label="user.name + ' (' + user.email + ')'"
                      :value="user._id"
                      class="ma-0 pa-0"
                      :data-testid="`comments__mention-user-checkbox-${user._id}`"
                    />
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </template>
          <span data-testid="comments__mention-tooltip-text">Mention users</span>
        </v-tooltip>
        <v-btn
          class="primaryBtn"
          :loading="sendingComment || sendingTagged"
          :disabled="!canSendComment || sendingComment || sendingTagged"
          @click="sendQuery(task)"
          data-testid="comments__send-btn"
        >
          Send
        </v-btn>
    </div>
  </v-card>
</template>
<script>
import { TAGS_LIST } from '@/modules/financialclose/utils/constants';
import { formatCommentMessage } from '@/views/common/commentMessageFormat';

export default {
  name: 'Comments',
  props: {
    conversationList: {
      type: Array,
      default: () => []
    },
    task: {
      type: Object,
      default: () => ({})
    },
    queryMsg: {
      type: String,
      default: ''
    },
    showprofile: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    /** When true, emit `send` instead of calling tasks/addConversation (e.g. Information Requests). */
    externalSend: {
      type: Boolean,
      default: false,
    },
    canComment: {
      type: Boolean,
      default: true,
    },
    /**
     * Optional mention candidates from the parent.
     * When provided, used instead of tasks/getFormDataV3 users.
     */
    users: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      localQueryMsg: this.queryMsg,
      sendingComment: false,
      showOptions: false,
      selectedUsers: [],
      sendingTagged: false,
      userSearch: '',
      failedProfileUrls: {},
    };
  },
  watch: {
    queryMsg(val) {
      this.localQueryMsg = val;
    },
    conversationList() {
      this.failedProfileUrls = {};
    },
  },
  computed: {
    usersListFlat() {
      // Prefer prop users when provided; otherwise formDataV3 / legacy usersList
      const source = Array.isArray(this.users)
        ? this.users
        : ((this.$store.getters['tasks/getFormDataV3'] || {}).users
          || this.$store.getters['tasks/getUsersList']
          || []);
      // Deduplicate by _id — formData can include the same user more than once,
      // which triggers Vue "Duplicate keys detected" on the mention list.
      const seen = new Set();
      return source.filter((user) => {
        const id = user && user._id;
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    },
    /**
     * O(1) user lookup map.
     * Built from prop users when provided; otherwise store-backed formDataV3 map.
     */
    userMap() {
      if (Array.isArray(this.users)) {
        const map = {};
        this.usersListFlat.forEach((user) => {
          if (user && user._id) map[user._id] = user;
        });
        return map;
      }
      return this.$store.getters['tasks/getUserMap'] || {};
    },
    filteredUsersList() {
      if (!this.userSearch) return this.usersListFlat;
      const search = this.userSearch.toLowerCase();
      return this.usersListFlat.filter(user =>
        (user.name || '').toLowerCase().includes(search) ||
        (user.email && user.email.toLowerCase().includes(search))
      );
    },
    metadata() {
      // Prefer metadata from task prop, fallback to store if needed
      if (this.task && this.task.metadata && this.task.metadata._id) {
        return this.task.metadata;
      } else if (this.task && this.task.project && this.task.project._id) {
        return this.task.project;
      } else if (this.$store.getters['tasks/getTaskListData'] && this.$store.getters['tasks/getTaskListData'].metadata && this.$store.getters['tasks/getTaskListData'].metadata._id) {
        return this.$store.getters['tasks/getTaskListData'].metadata;
      }
      return {};
    },
    tagsList() {
      return TAGS_LIST || [];
    },
    /** True when comment text has non-whitespace content. */
    canSendComment() {
      return Boolean(this.localQueryMsg && this.localQueryMsg.trim());
    },
  },
  mounted() {
    if (this.externalSend) return;
    // Users are already available from formDataV3 (fetched by fetchTask/getAllTasks).
    // Only call legacy getUsersFormData if formDataV3 is not populated yet.
    const formData = this.$store.getters['tasks/getFormDataV3'];
    if (!formData || !formData.users || !formData.users.length) {
      this.$store.dispatch('tasks/getUsersFormData');
    }
  },
  methods: {
    formatCommentMessage,
    isSelfMessage(item) {
      const itemUserId = item?.user;
      const storeUser = this.$store.state.user || {};
      const storeIds = [storeUser.id, storeUser._id].filter(Boolean).map(String);
      return storeIds.includes(String(itemUserId));
    },
    shouldShowAvatar(index) {
      const item = this.conversationList[index];
      const previous = this.conversationList[index - 1];
      return !previous || String(previous.user) !== String(item.user);
    },
    getUserName(item) {
      if (item?.user_detail?.name) return item.user_detail.name;
      if (this.isSelfMessage(item)) {
        return this.$store.state.user?.name || this.$store.state.user?.email || 'NA';
      }
      return item?.user_detail?.email || 'NA';
    },
    getUserInitials(item) {
      const name = this.getUserName(item);
      if (name && name !== 'NA') return name.charAt(0).toUpperCase();
      const email = item?.user_detail?.email
        || (this.isSelfMessage(item) ? this.$store.state.user?.email : null);
      if (email) return email.charAt(0).toUpperCase();
      return '?';
    },
    shouldShowProfileImage(item) {
      if (!this.showprofile) return false;
      const url = item?.user_detail?.profile_url;
      if (!url || this.failedProfileUrls[url]) return false;
      return true;
    },
    onProfileImageError(item) {
      const url = item?.user_detail?.profile_url;
      if (url) this.$set(this.failedProfileUrls, url, true);
    },
    async sendQuery(task) {
      if (!this.canSendComment) return;
      this.sendingComment = true;
      let msg = this.localQueryMsg;
      let coloredTaggedNames = '';
      // If tagged users, prepend their names to the comment
      if (this.selectedUsers.length) {
        const taggedNamesArr = this.selectedUsers
          .map(id => {
            const user = this.userMap[id];
            return user ? `<span class='tagged-name'>${user.name}</span>` : '';
          })
          .filter(Boolean);
        // Only tagged names colored, not the value
        coloredTaggedNames = taggedNamesArr.join(', ');
        msg = coloredTaggedNames + (coloredTaggedNames ? ' ' : '') + this.localQueryMsg;
      }

      if (this.externalSend) {
        const plainText = this.localQueryMsg;
        this.$emit('send', {
          message: msg,
          plainText,
          taggedUserIds: [...this.selectedUsers],
          complete: (ok) => {
            this.sendingComment = false;
            if (ok) {
              this.localQueryMsg = '';
              this.selectedUsers = [];
            }
          },
        });
        return;
      }

      const dataObject = {
        type: "string",
        value: msg,
        status: "",
      };
      if (!this.metadata._id) {
        this.sendingComment = false;
        this.$store.commit('sbError', 'No project found: metadata._id is missing');
        return;
      }
      try {
        const result = await this.$store.dispatch('tasks/addConversation', {
          metadataId: this.metadata._id,
          taskId: task._id,
          dataObject,
          context: this
        });
        if (!result || !result.success) {
          return;
        }
        // Add the new comment to the conversationList immediately
        const user = this.$store.state.user.id;
        const user_detail = this.$store.state.user;
        const newComment = {
          value: dataObject.value,
          user,
          user_detail,
          created_at: new Date().toISOString()
        };
        if (Array.isArray(this.conversationList)) {
          this.conversationList.push(newComment);
        }
        this.$emit('refresh');
        this.localQueryMsg = '';
        // Send tagged notification if users and comment are present
        if (this.selectedUsers.length && msg) {
          this.sendingTagged = true;
          try {
            const taggedUsers = this.selectedUsers.map(id => {
              const user = this.userMap[id];
              return { name: user ? user.name : id, email: user && user.email ? user.email : '' };
            });
            const taggedResult = await this.$store.dispatch('tasks/triggerTaggedNotification', {
              taggedUsers,
              msg,
              task: this.task
            });
            if (taggedResult && taggedResult.success) {
              this.selectedUsers = [];
            }
          } finally {
            this.sendingTagged = false;
          }
        } else {
          this.selectedUsers = [];
        }
      } catch (err) {
        this.$store.commit(
          "sbError",
          err.message || err || "Unknown error!"
        );
        console.log(err);
      } finally {
        this.sendingComment = false;
      }
    },
  },
}

</script>

<style lang="scss">
@import './Comments.scss';
</style>